import {
    AfterViewInit,
    Component,
    HostListener,
    input,
    ViewChildren,
    QueryList,
    ElementRef,
    PLATFORM_ID,
    inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

import { ComponentsModule, ModalService } from '@jht/brand-components';
import { PipesModule } from '@jht/brand-pipes';
import { TranslateModule } from '@jht/brand-translate';
import { DirectivesModule } from '@jht/brand-directives';
import { BuilderComponent, BuilderField } from '@jht/brand-page-builder/core';

import { VideoData, VideoService } from '../../services/video.service';

import { SafeResourceUrl } from '@angular/platform-browser';

@Component({
    selector: 'vision2-card',
    imports: [
        CommonModule,
        ComponentsModule,
        PipesModule,
        DirectivesModule,
        TranslateModule,
    ],
    templateUrl: './card.component.html',
    styleUrl: './card.component.scss',
})
@BuilderComponent({
    label: 'Card',
    category: 'Content',
    sectionClass: 'container py-4 py-lg-5',
})
export class CardComponent implements AfterViewInit {
    @BuilderField({ group: 'button' }) buttonUrl = input<string>();
    @BuilderField({ group: 'button' }) buttonText = input<string>();
    cardClass = input<string>();
    @BuilderField({ type: 'color', options: ['bg-gray-30', 'bg-transparent'] })
    cardColor = input<string>('bg-gray-30');
    @BuilderField({ type: 'richtext' }) description = input<string>();
    headingClass = input<string>();
    @BuilderField({ description: 'Heading level rendered (h1-h5).' })
    headingValue = input<number>();
    image = input<string>();
    imageAlt = input<string>();
    imageClass = input<string>();
    @BuilderField({
        type: 'string',
        help: 'Responsive "sizes" attribute for the optimized image, e.g. "(min-width: 768px) 50vw, 100vw".',
    })
    imageSizes = input<string>('');
    @BuilderField({ ignore: true }) imageOptimization = input<boolean>(true);
    @BuilderField({
        group: 'video',
        help: 'Vimeo video id. Shows a button that opens the video in a modal.',
    })
    modalVideoId = input<string>();
    @BuilderField({ group: 'link' }) linkUrl = input<string>();
    @BuilderField({ group: 'link' }) linkText = input<string>();
    title = input<string>();
    @BuilderField({
        type: 'order',
        label: 'Actions',
        help: 'Drag to change the order these render in.',
    })
    actionOrder = input<string[]>(['video', 'button', 'link']);

    @ViewChildren('cardImage')
    cardImages!: QueryList<ElementRef>;
    @ViewChildren('cardHeading')
    cardHeadings!: QueryList<ElementRef>;
    @ViewChildren('cardDescription')
    cardDescriptions!: QueryList<ElementRef>;
    @ViewChildren('cardButton')
    cardButtons!: QueryList<ElementRef>;
    @ViewChildren('cardLink')
    cardLinks!: QueryList<ElementRef>;
    @ViewChildren('cardContent')
    cardContents!: QueryList<ElementRef>;
    @ViewChildren('cardModalLink')
    cardModalLinks!: QueryList<ElementRef>;

    modalVideoUrl!: VideoData;
    currentModal = '';
    modalVideoLoaded: boolean = false;
    isBrowser: boolean = false;
    platformId = inject(PLATFORM_ID);

    constructor(
        private modalService: ModalService,
        private videoService: VideoService,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscPressed(event: Event) {
        this.closeModal(this.currentModal);
    }

    ngAfterViewInit() {
        if (this.modalVideoId()) {
            this.initializeModalVideos();
        }
    }

    private initializeModalVideos() {
        const modalVideoInput = this.modalVideoId();
        const modalVideoOptions = {
            autoplay: true,
            autopause: true,
            background: false,
            controls: true,
            loop: false,
            muted: false,
            responsive: false,
        };
        if (modalVideoInput) {
            this.videoService
                .getVideoData(modalVideoInput, { ...modalVideoOptions })
                .then((data: any) => {
                    if (data) {
                        this.modalVideoUrl = data;
                    }
                });
        }
    }

    openModal(id: string): void {
        if (id) {
            this.currentModal = id;
            this.modalVideoLoaded = true;
            this.modalService.open(id);
        }
    }

    closeModal(id: string): void {
        if (id) {
            this.currentModal = '';
            this.modalVideoLoaded = false;
            this.modalService.close(id);
        }
    }
}
